(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-faj-contact-drawer]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class FAJContactDrawer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-faj-contact-panel]');
      this.overlay = root.querySelector('[data-faj-contact-overlay]');
      this.openButtons = Array.from(root.querySelectorAll('[data-faj-contact-open]'));
      this.closeButtons = Array.from(root.querySelectorAll('[data-faj-contact-close]'));
      this.lastFocusedElement = null;
      this.isOpen = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);

      this.bind();
    }

    bind() {
      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => this.open(button));
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.overlay?.addEventListener('click', () => this.close());
      this.panel?.addEventListener('transitionend', this.handleTransitionEnd);

      // Any site button/link with data-faj-contact-open can open this drawer.
      document.addEventListener('click', this.handleDocumentClick);
    }

    destroy() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.panel?.removeEventListener('transitionend', this.handleTransitionEnd);

      if (this.isOpen) {
        document.documentElement.classList.remove('faj-contact-drawer-scroll-lock');
      }
    }

    handleDocumentClick(event) {
      const externalOpenButton = event.target.closest(
        '[data-faj-contact-open]:not(.faj-contact-drawer__trigger)'
      );

      if (!externalOpenButton || this.root.contains(externalOpenButton)) return;

      event.preventDefault();
      this.open(externalOpenButton);
    }

    open(opener) {
      if (this.isOpen || !this.panel || !this.overlay) return;

      this.isOpen = true;
      this.lastFocusedElement = opener || document.activeElement;

      this.overlay.hidden = false;
      this.panel.setAttribute('aria-hidden', 'false');
      this.root.classList.add('is-open');
      document.documentElement.classList.add('faj-contact-drawer-scroll-lock');

      this.openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        const firstFocusable = this.getFocusableElements()[0];
        (firstFocusable || this.panel).focus({ preventScroll: true });
      });

      this.root.dispatchEvent(
        new CustomEvent('faj:contact-drawer:open', { bubbles: true })
      );
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen || !this.panel) return;

      this.isOpen = false;
      this.root.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('faj-contact-drawer-scroll-lock');

      this.openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      document.removeEventListener('keydown', this.handleKeydown);

      if (restoreFocus && this.lastFocusedElement?.focus) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      this.root.dispatchEvent(
        new CustomEvent('faj:contact-drawer:close', { bubbles: true })
      );
    }

    handleTransitionEnd(event) {
      if (event.target !== this.panel || this.isOpen || !this.overlay) return;
      this.overlay.hidden = true;
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = this.getFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements() {
      return Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) =>
          !element.hasAttribute('hidden') &&
          element.getAttribute('aria-hidden') !== 'true' &&
          element.offsetParent !== null
      );
    }
  }

  const instances = new WeakMap();

  function init(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new FAJContactDrawer(root));
  }

  function destroy(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    if (root) destroy(root);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!root) return;

    const instance = instances.get(root);
    instance?.open(root.querySelector('[data-faj-contact-open]'));
  });
})();
